import React, { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { LegacyPopup } from 'app/home/general-components/Popup/Popup';
import { useLocalizedStrings } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from '../general-components/HistoryStackContext';
import { OTAType, useOTAReportPage } from '../overlay-pages/device/deivce-detail/OTAReport';
import useReminder from '../general-components/Reminder';

import { ReactComponent as IconWarning } from 'images/general/icon-warning-red.svg';

import './Popups.scss'


export function HubUpdatePopup({ homesManager, gateway }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const changeHome = useCallback(() => {
        hs.push("/homes/switch");
    }, [hs]);

    const s = useLocalizedStrings();

    const activeHome = homesManager.activeHome;
    const phoneNumber = activeHome.phoneNumber;
    const dontShowUpdatePopupBefore = activeHome.dontShowUpdatePopupBefore;
    const snoozUpdatePopup = homesManager.snoozUpdatePopup;

    const gwUpdateInfo = gateway.gwUpdateInfo;
    const criticalSituation = gateway.gatewayCriticallyNeedsUpdate;
    const isPopupNeeded = (
        criticalSituation || (
            (
                (gwUpdateInfo.isNewerVersionAvailable === undefined) ||
                (gwUpdateInfo.updateSeverityLevel === undefined)
            )
                ? false :
                gwUpdateInfo.isNewerVersionAvailable && (gwUpdateInfo.updateSeverityLevel > 1)
        )
    );
    const canOTAHubFromClient = (
        (gateway.currentUser.permissions.setting) &&
        (gateway.negotiatedProtocol.canOTAHubFromClient) &&
        (gwUpdateInfo.canUpdateFromClient === true)
    );

    const isPopupActive = useMemo(() => {
        if (isPopupNeeded === undefined) {
            return false;
        } else if (isPopupNeeded === false) {
            return false;
        } else {
            const timeToShow =
                (dontShowUpdatePopupBefore === undefined) ||
                (Date.now() > dontShowUpdatePopupBefore);

            return criticalSituation || timeToShow;
        }
    }, [isPopupNeeded, dontShowUpdatePopupBefore, criticalSituation]);

    const redMessage = criticalSituation || (gwUpdateInfo.updateSeverityLevel > 2);

    const startOTA = useOTAReportPage(gateway.otaProgressController, OTAType.hub, undefined);

    let message = "";
    if (criticalSituation) {
        message = s.updateNotification.criticalMessage;
    } else {
        if ((gateway.negotiatedProtocol.canOTAHubFromClient) && (gwUpdateInfo.canUpdateFromClient === true)) {
            if (gateway.currentUser.permissions.setting) {
                message = s.updateNotification.message;
            } else {
                message = s.updateNotification.nonAdminMessage;
            }
        } else {
            message = s.updateNotification.nonOTAMessage;
        }
    }

    return (
        <LegacyPopup
            show={isPopupActive && (p.path !== "/homes/switch")}
            dismiss={() => { }} // can not dismiss
            baseZIndex={50} name="update-popup">

            <div className="update-popup">
                <div className="header">
                    {s.updateNotification.hubTitle}
                </div>

                <div className={redMessage ? "body red" : "body"}>
                    {redMessage ? <IconWarning className="icon" /> : null}
                    {message}
                </div>

                {
                    criticalSituation ? (
                        <div className="button full light" onClick={() => { changeHome() }}>
                            {s.updateNotification.changeHome}
                        </div>
                    ) : (
                        canOTAHubFromClient ? (
                            <Fragment>
                                <div className="button blue c1-5" onClick={() => { snoozUpdatePopup(phoneNumber); startOTA() }}>
                                    {s.updateNotification.update}
                                </div>
                                <div className="button light c5-7" onClick={() => { snoozUpdatePopup(phoneNumber) }}>
                                    {s.updateNotification.later}
                                </div>
                            </Fragment>
                        ) : (
                            <div className="button full" onClick={() => { snoozUpdatePopup(phoneNumber) }}>
                                {s.updateNotification.gotIt}
                            </div>
                        )
                    )
                }
            </div>
        </LegacyPopup>
    )
}

export function ClientUpdatePopup({ swController }) {
    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();

    const [itIsTimeToShowTheMessage, snoozUpdatePopup] = useReminder("ClientUpdatePopup", 1)
    const isPopupActive = (swController.updateIsReady === true) && itIsTimeToShowTheMessage;

    return (
        <LegacyPopup
            show={isPopupActive && (p.path !== "/homes/switch")}
            dismiss={() => { }} // can not dismiss
            baseZIndex={50} name="client-update-popup">

            <div className="update-popup">
                <div className="header">
                    {s.updateNotification.clientTitle}
                </div>
                <div className="body">
                    {s.updateNotification.clientMessage}
                </div>
                <div className="button blue c1-5" onClick={swController.reloadForUpdate}>
                    {s.updateNotification.update}
                </div>
                <div className="button light c5-7" onClick={snoozUpdatePopup}>
                    {s.updateNotification.later}
                </div>
            </div>
        </LegacyPopup>
    )
}

export function AskPushPopup({ swController }) {
    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();

    const [itIsTimeToShowTheMessage, snoozPopup] = useReminder("AskPushPopup", 3)
    const isPopupActive = (swController.shouldAskPushPermission === true) && itIsTimeToShowTheMessage;

    return (
        <LegacyPopup
            show={isPopupActive && (p.path !== "/homes/switch")}
            dismiss={() => { }} // can not dismiss
            baseZIndex={50} name="ask-push-popup">

            <div className="update-popup">
                <div className="header">
                    {s.askPushPopup.title}
                </div>
                <div className="body">
                    {s.askPushPopup.body}
                </div>
                <div className="button blue c1-5" onClick={() => {
                    swController.askPushPermission();
                    snoozPopup();
                }}>
                    {s.askPushPopup.allow}
                </div>
                <div className="button light c5-7" onClick={snoozPopup}>
                    {s.askPushPopup.later}
                </div>
            </div>
        </LegacyPopup>
    )
}

export function AvailabilityErrorPopup({ homeAvailabilityCriticalError, logoutFromSolo }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const changeHome = useCallback(() => {
        hs.push("/homes/switch");
    }, [hs]);

    const s = useLocalizedStrings();
    const ap = s.availabilityPopup;

    const messageCode = homeAvailabilityCriticalError.message;
    const messageTime = homeAvailabilityCriticalError.time;

    const [messageTimeToSnooz, setMessageTimeToSnooz] = useState(undefined);

    const [show, header, body, redBody] = useMemo(() => {
        if (messageCode === "removed") {
            return [true, ap.removed.title, ap.removed.body, true]
        } else if (messageCode === "offline") {
            return [messageTime !== messageTimeToSnooz, ap.offline.title, ap.offline.body, false]
        } else if (messageCode === "shouldlogin") {
            return [true, ap.shouldlogin.title, ap.shouldlogin.body, true]
        }
        return [false, undefined, undefined, undefined]
    }, [messageCode, ap, messageTimeToSnooz, messageTime]);

    const buttons = useMemo(() => {
        if (messageCode === "removed") {
            return (
                <div className="button full light" onClick={changeHome}>
                    {ap.changeHome}
                </div>
            )
        } else if (messageCode === "offline") {
            return (
                <Fragment>
                    <div className="button light c1-4" onClick={() => { setMessageTimeToSnooz(messageTime) }}>
                        {ap.ok}
                    </div>
                    <div className="button light c4-7" onClick={changeHome}>
                        {ap.changeHome}
                    </div>
                </Fragment>
            )
        } else if (messageCode === "shouldlogin") {
            return (
                <div className="button full light" onClick={() => { logoutFromSolo(); changeHome(); }}>
                    {ap.ok}
                </div>
            )
        }
    }, [messageCode, ap, changeHome, messageTime, logoutFromSolo]);

    const _show = show && (p.path !== "/homes/switch");

    return (
        <LegacyPopup
            show={_show}
            dismiss={() => { }} // can not dismiss
            baseZIndex={50} name="availability-popup">

            <div className="general-popup">
                <div className="header">
                    {header}
                </div>

                <div className={redBody ? "body red" : "body"}>
                    {redBody ? <IconWarning className="icon" /> : null}
                    {body}
                </div>

                {buttons}
            </div>
        </LegacyPopup>
    )
}